import { MenuItem } from 'primeng/api';
import { getChildItems, menuIcon, normalizeRouterLink, normalizeUrl } from '../shared/page-layout/app-menu.utils';
import { SpringboardQuickAction, SpringboardTab, SpringboardTile } from './dashboard-springboard.model';

const QUICK_ACTION_KEYWORDS = [
    'create',
    'add',
    'new',
    'express',
    'bulk upload',
    'run bulk',
    'approve',
    'approval',
    'post expense',
    'manual receivable',
    'manual payable',
    'manual invoice draft',
    'non-po bill draft',
    'notifications',
    'tasks',
    'incidents'
];

const DEFAULT_MAIN_ROUTE_ORDER = [
    // Operations & Shipments
    '/shipment/company',
    '/shipment',
    '/shipment/deliveries',
    '/warehousing/overview',
    '/warehousing/stock',
    '/fleet',
    '/fleet/vehicles',
    '/container-tracking/overview',
    '/container-tracking/cases',
    '/clearance/list',
    '/clearance',
    // Commercial
    '/crm/clients',
    '/crm/leads',
    '/crm/quotations',
    '/contracts',
    // Finance
    '/accounts',
    '/accounts/enterprise/receivables/generated-invoices',
    '/accounts/enterprise/payables/generated-bills',
    '/accounts/enterprise/banking/bank-accounts',
    '/accounts/enterprise/bank-reconciliation/dashboard',
    '/chart-of-accounts',
    '/expense',
    // HR & Workspace
    '/hr/employees',
    '/hr/leave',
    '/my-hr',
    '/workspace/petty-cash',
    '/reports',
    '/company/users-and-roles',
    '/company'
];

interface LeafMenuItem {
    id: string;
    label: string;
    icon: string;
    route: string;
    parentGroup?: string;
    rootCategoryLabel: string;
}

export function buildSpringboardTabs(
    menuItems: MenuItem[],
    frequentRoutes: string[] = [],
    mainTabLabel: string = 'Main'
): SpringboardTab[] {
    const visibleRoots = menuItems.filter(item => item.visible !== false && item.label !== 'Dashboard');
    const allLeaves: LeafMenuItem[] = [];

    const domainTabs: SpringboardTab[] = [];

    for (const root of visibleRoots) {
        const rootLabel = (root.label || 'Other').trim();
        const rootId = slugify(rootLabel);

        const leaves = extractLeafItems(root, rootLabel);
        if (!leaves.length) {
            continue;
        }

        allLeaves.push(...leaves);

        const quickActions: SpringboardQuickAction[] = [];
        const tiles: SpringboardTile[] = [];
        const subGroupSet = new Set<string>();

        for (const leaf of leaves) {
            if (isQuickAction(leaf)) {
                quickActions.push({
                    id: leaf.id,
                    label: leaf.label,
                    icon: leaf.icon,
                    route: leaf.route
                });
            } else {
                if (leaf.parentGroup && leaf.parentGroup !== rootLabel) {
                    subGroupSet.add(leaf.parentGroup);
                }
                tiles.push({
                    id: leaf.id,
                    label: leaf.label,
                    icon: leaf.icon,
                    route: leaf.route,
                    group: leaf.parentGroup !== rootLabel ? leaf.parentGroup : undefined
                });
            }
        }

        // Only include if there are tiles or quick actions
        if (tiles.length || quickActions.length) {
            domainTabs.push({
                id: rootId,
                label: rootLabel,
                icon: root.icon || getFallbackIcon(rootLabel),
                quickActions,
                tiles,
                subGroups: subGroupSet.size > 1 ? ['All', ...Array.from(subGroupSet)] : []
            });
        }
    }

    // Now construct the MAIN tab
    const mainTab = buildMainTab(allLeaves, frequentRoutes, mainTabLabel);

    return [mainTab, ...domainTabs];
}

function buildMainTab(
    allLeaves: LeafMenuItem[],
    frequentRoutes: string[],
    mainTabLabel: string
): SpringboardTab {
    const tileByRoute = new Map<string, SpringboardTile>();
    const quickActionByRoute = new Map<string, SpringboardQuickAction>();

    for (const leaf of allLeaves) {
        const normalized = normalizeUrl(leaf.route);
        if (isQuickAction(leaf)) {
            if (!quickActionByRoute.has(normalized)) {
                quickActionByRoute.set(normalized, {
                    id: leaf.id,
                    label: leaf.label,
                    icon: leaf.icon,
                    route: leaf.route
                });
            }
        } else {
            if (!tileByRoute.has(normalized)) {
                tileByRoute.set(normalized, {
                    id: leaf.id,
                    label: leaf.label,
                    icon: leaf.icon,
                    route: leaf.route,
                    group: leaf.rootCategoryLabel
                });
            }
        }
    }

    const mainTiles: SpringboardTile[] = [];
    const usedTileRoutes = new Set<string>();

    // 1. First in the list: frequently accessed pages
    for (const route of frequentRoutes) {
        const normalized = normalizeUrl(route);
        const tile = tileByRoute.get(normalized);
        if (tile && !usedTileRoutes.has(normalized)) {
            mainTiles.push({
                ...tile,
                isFrequent: true
            });
            usedTileRoutes.add(normalized);
        }
    }

    // 2. Pre-populated backfill up to 15 tiles from curated defaults
    for (const defaultRoute of DEFAULT_MAIN_ROUTE_ORDER) {
        if (mainTiles.length >= 15) {
            break;
        }
        const normalized = normalizeUrl(defaultRoute);
        const tile = tileByRoute.get(normalized);
        if (tile && !usedTileRoutes.has(normalized)) {
            mainTiles.push(tile);
            usedTileRoutes.add(normalized);
        }
    }

    // 3. Fallback: if still under 10 tiles, take from any remaining permitted tiles
    if (mainTiles.length < 10) {
        for (const [route, tile] of tileByRoute.entries()) {
            if (mainTiles.length >= 15) {
                break;
            }
            if (!usedTileRoutes.has(route)) {
                mainTiles.push(tile);
                usedTileRoutes.add(route);
            }
        }
    }

    // Quick actions for main tab
    const mainQuickActions: SpringboardQuickAction[] = [];
    const usedQaRoutes = new Set<string>();

    // Frequent quick actions first
    for (const route of frequentRoutes) {
        const normalized = normalizeUrl(route);
        const qa = quickActionByRoute.get(normalized);
        if (qa && !usedQaRoutes.has(normalized)) {
            mainQuickActions.push(qa);
            usedQaRoutes.add(normalized);
        }
    }

    // Add common quick actions if available
    const commonQaRoutes = [
        '/shipment/create',
        '/shipment/create/express',
        '/productivity/tasks',
        '/productivity/notifications',
        '/productivity/incidents',
        '/expense/add',
        '/expense/approvals',
        '/crm/leads',
        '/clearance/add'
    ];

    for (const route of commonQaRoutes) {
        if (mainQuickActions.length >= 7) {
            break;
        }
        const qa = quickActionByRoute.get(route);
        if (qa && !usedQaRoutes.has(route)) {
            mainQuickActions.push(qa);
            usedQaRoutes.add(route);
        }
    }

    return {
        id: 'main',
        label: mainTabLabel,
        icon: 'pi pi-home',
        quickActions: mainQuickActions,
        tiles: mainTiles,
        subGroups: []
    };
}

function extractLeafItems(item: MenuItem, rootCategoryLabel: string, parentGroup?: string): LeafMenuItem[] {
    const results: LeafMenuItem[] = [];
    const children = getChildItems(item);

    const directRoute = normalizeRouterLink(item.routerLink);
    const itemLabel = (item.label || '').trim();

    if (children.length === 0) {
        if (directRoute && directRoute !== '/dashboard') {
            results.push({
                id: directRoute,
                label: disambiguateLabel(itemLabel, parentGroup || rootCategoryLabel),
                icon: menuIcon(item),
                route: directRoute,
                parentGroup,
                rootCategoryLabel
            });
        }
    } else {
        // Has children
        for (const child of children) {
            const currentGroup = parentGroup || (itemLabel !== rootCategoryLabel ? itemLabel : undefined);
            results.push(...extractLeafItems(child, rootCategoryLabel, currentGroup));
        }
    }

    return results;
}

function disambiguateLabel(label: string, parentGroup: string): string {
    const cleanLabel = label.trim();
    if (cleanLabel.toLowerCase() === 'dashboard' && parentGroup) {
        return `${parentGroup} Dashboard`;
    }
    if (cleanLabel.toLowerCase() === 'overview' && parentGroup) {
        return `${parentGroup} Overview`;
    }
    return cleanLabel;
}

function isQuickAction(leaf: LeafMenuItem): boolean {
    const labelLower = leaf.label.toLowerCase();
    const routeLower = leaf.route.toLowerCase();

    return QUICK_ACTION_KEYWORDS.some(kw => labelLower.startsWith(kw) || labelLower.includes(kw))
        || routeLower.endsWith('/add')
        || routeLower.endsWith('/create')
        || routeLower.endsWith('/new')
        || routeLower.endsWith('/approvals')
        || routeLower.endsWith('/post');
}

function slugify(text: string): string {
    return text.toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function getFallbackIcon(label: string): string {
    const slug = slugify(label);
    if (slug.includes('workspace')) return 'fa-solid fa-table-columns';
    if (slug.includes('commercial')) return 'fa-solid fa-handshake';
    if (slug.includes('project')) return 'fa-solid fa-layer-group';
    if (slug.includes('warehousing')) return 'fa-solid fa-boxes-stacked';
    if (slug.includes('shipment')) return 'fa-solid fa-truck-ramp-box';
    if (slug.includes('fleet')) return 'fa-solid fa-truck-fast';
    if (slug.includes('finance')) return 'fa-solid fa-cash-register';
    if (slug.includes('hr')) return 'fa-solid fa-users-gear';
    if (slug.includes('audit')) return 'fa-solid fa-clipboard-check';
    if (slug.includes('admin')) return 'fa-solid fa-gear';
    return 'pi pi-th-large';
}


